import React, { useEffect, useState } from "react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Document, BLOCKS } from "@contentful/rich-text-types";
import { useParams } from "react-router-dom";

const locale = "en-US";

interface BlogPostContent {
  title: string;
  date: string;
  content: Document;
  hashtags: string[];
  image: string | null;
  status: "published" | "unpublished" | "archived";
}

interface ManifestEntry {
  id: string;
  title: string;
  date: string;
  hashtags: string[];
  image: string | null;
  status: "published" | "unpublished" | "archived";
}

const BlogPost = () => {
  const [post, setPost] = useState<BlogPostContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { slug } = useParams<{ slug?: string }>(); // slug may be undefined

  const bucket = process.env.REACT_APP_FIREBASE_BUCKET;
  const contentPath = process.env.REACT_APP_CONTENT_PATH;

  useEffect(() => {
    // ✅ Handle missing slug early
    if (!slug) {
      setError("No post selected.");
      setLoading(false);
      return;
    }

    // ✅ Handle missing Firebase config
    if (!bucket || !contentPath) {
      setError("Firebase environment variables are not set.");
      setLoading(false);
      return;
    }

    const manifestUrl = `https://storage.googleapis.com/${bucket}/${encodeURIComponent(
      contentPath + "/manifest.json"
    )}`;

    const postJsonUrl = (id: string) =>
      `https://storage.googleapis.com/${bucket}/${encodeURIComponent(
        contentPath + "/" + id + ".json"
      )}`;

    async function fetchPost() {
      try {
        setLoading(true);
        setError(null);

        // Fetch manifest first
        const manifestResp = await fetch(manifestUrl);
        if (!manifestResp.ok) throw new Error("Failed to load manifest");
        const manifest: ManifestEntry[] = await manifestResp.json();

        const entry = manifest.find((m) => m.id === slug);
        if (!entry || entry.status !== "published") {
          setPost(null);
          setError("Post not found or not published.");
          return;
        }

        // Fetch post JSON
        const postResp = await fetch(postJsonUrl(slug!));
        if (!postResp.ok) {
          throw new Error(`Failed to load post: ${postResp.status}`);
        }
        const data = await postResp.json();

        setPost({
          title: data.fields?.title?.[locale] ?? "Untitled",
          content: data.fields?.content?.[locale] ?? {
            nodeType: "document",
            content: [],
          },
          hashtags: Array.isArray(data.fields?.hashtags?.[locale])
            ? data.fields.hashtags[locale]
            : [],
          date: entry.date,
          image: data.firebaseHeaderImage ?? entry.image ?? null, // fallback to manifest image
          status: "published",
        });
      } catch (err: any) {
        setPost(null);
        setError(err.message || "Error loading post");
      } finally {
        setLoading(false);
      }
    }

    fetchPost();
  }, [slug, bucket, contentPath]);

  if (loading)
    return <div className="alert alert-info text-center">Loading...</div>;
  if (error)
    return <div className="alert alert-danger text-center">Error: {error}</div>;
  if (!post)
    return <div className="alert alert-warning text-center">Not Found</div>;

  const renderOptions = {
    renderNode: {
      [BLOCKS.QUOTE]: (_node: any, children: React.ReactNode) => (
        <blockquote className="blockquote border-start ps-3 my-4">
          {children}
        </blockquote>
      ),
    },
  };

  return (
    <article className="container pt-5">
      <div className="col">
        {post.image && (
          <div className="row mb-4 text-center g-0">
            <img
              src={post.image}
              alt={post.title}
              className="img-fluid rounded shadow"
            />
          </div>
        )}

        {post.title && (
          <div className="row mb-3">
            <h1 className="fw-bold text-primary">{post.title}</h1>
            <h4>{new Date(post.date).toLocaleDateString()}</h4>
          </div>
        )}

        <div className="row">
          <hr />
        </div>

        <div className="row">
          <div className="mt-4">
            {post.content?.nodeType === "document" &&
            Array.isArray(post.content?.content) ? (
              <div className="fs-5">
                {documentToReactComponents(post.content, renderOptions)}
              </div>
            ) : (
              <p className="text-muted">No content available.</p>
            )}
          </div>
        </div>

        <div className="row">
          <hr />
        </div>

        {post.hashtags.length > 0 && (
          <div className="row">
            <section className="mt-4">
              <div className="d-flex flex-wrap gap-3">
                {post.hashtags.map((tag, index) => (
                  <div
                    className="hashtag-link"
                    key={index}
                    onClick={() =>
                      window.open(
                        `https://www.facebook.com/hashtag/${tag}?locale=en_GB`,
                        "_blank"
                      )
                    }
                  >
                    <span className="hashtag-small dark">#{tag}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </article>
  );
};

export default BlogPost;
